import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";

import { Alert, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Slide } from "@mui/material";

import { InputField } from "../components/InputField.tsx";

import { setClient } from "../Store/roiStore.ts";
import { PAR_SOLUTIONS, PAR360_PLUS, PAR360_PREMIUM, hasPar360Solution } from "../constants.ts";
import {
  CUSTOMER_SITUATION,
  resolveCustomerSituation,
} from "../utils/customerSituation.ts";
import "../styles/pages/client-details.scss";

export const ClientDetails = () => {
  const client = useSelector((state) => state.inputFields.client);
  const { dispatch } = useStore();
  const [isNameValid, setIsNameValid] = useState(true);
  const firstUpdate = useRef(true);

  const selectedSolutions: string[] = client?.currentParSolutions ?? [];
  const par360Selected = hasPar360Solution(selectedSolutions);
  const situation = resolveCustomerSituation(client);

  const updateClient = (updates) => {
    dispatch(
      setClient({
        ...client,
        ...updates,
      })
    );
  };

  const handleSolutionChange = (solution) => {
    const isSelected = selectedSolutions.includes(solution);
    let nextSelections = isSelected
      ? selectedSolutions.filter((item) => item !== solution)
      : [...selectedSolutions, solution];

    // PAR 360 Premium is a superset of PAR 360 Plus: selecting Premium forces Plus
    // on as well (and the Plus checkbox is locked while Premium stays selected).
    if (solution === PAR360_PREMIUM && !isSelected && !nextSelections.includes(PAR360_PLUS)) {
      nextSelections = [...nextSelections, PAR360_PLUS];
    }

    const stillHasPar360 = hasPar360Solution(nextSelections);
    updateClient({
      currentParSolutions: nextSelections,
      customerSituation: stillHasPar360 ? situation : CUSTOMER_SITUATION.NEW,
      isPar360AddOn: stillHasPar360 && situation === CUSTOMER_SITUATION.PAR360_ADDON,
    });
  };

  const handleSituationChange = (nextSituation: string) => {
    updateClient({
      customerSituation: nextSituation,
      isPar360AddOn: nextSituation === CUSTOMER_SITUATION.PAR360_ADDON,
    });
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    setIsNameValid(client?.name.length > 0);
  }, [client?.name]);

  return (
    <Slide direction="right" in={true} mountOnEnter unmountOnExit>
      <div className="client-details-page">
        <h1>Client Details</h1>
        <p className="client-details-instructions">
          This ROI analysis will include hospital facilities where selected PAR solutions will be deployed. For each facility, you will be asked to enter baseline data used by the tool to estimate savings and ROI.
        </p>
        <div className="client-details-form">
            <InputField
              placeholder="Client name, eg.: Northern Light Healthcare"
              label="Client Name (IDN)"
              value={client?.name}
              error={!isNameValid}
              onChange={(event) => updateClient({ name: event.target.value })}
            />
            <InputField
              placeholder="Enter annual NPSR"
              label="Client's Annual Net Patient Services Revenue (All facilities combined):"
              value={client?.annualNetPatientServiceRevenue ?? 0}
              type="number"
              prefix="$"
              onValueInputChange={(name, value) =>
                updateClient({ annualNetPatientServiceRevenue: Number(value ?? 0) })
              }
            />

            <div className="client-details-section">
              <div className="client-details-section-title">
                What PAR solutions are included in this ROI analysis?
              </div>
              <FormGroup>
                {PAR_SOLUTIONS.map((solution) => {
                  const selected = selectedSolutions;
                  const premiumSelected = selected.includes(PAR360_PREMIUM);
                  const lockPlus = solution === PAR360_PLUS && premiumSelected;
                  return (
                    <React.Fragment key={solution}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.includes(solution) || lockPlus}
                            disabled={lockPlus}
                            onChange={() => handleSolutionChange(solution)}
                          />
                        }
                        label={
                          lockPlus ? `${solution} (included with PAR 360 Premium)` : solution
                        }
                      />
                      {solution === PAR360_PREMIUM && (
                        <div className="client-details-solution-note">
                          Enables the Package Delivery freight cost savings
                          calculation on the Investment step (includes PAR 360 Plus).
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </FormGroup>
            </div>

            <div className="client-details-section">
              <div className="client-details-section-title">
                Customer situation
              </div>
              <RadioGroup
                value={situation}
                onChange={(event) => handleSituationChange(event.target.value)}
              >
                <FormControlLabel
                  value={CUSTOMER_SITUATION.NEW}
                  control={<Radio />}
                  label="New deployment (net-new scales)"
                />
                <div className="client-details-solution-note">
                  Standard ROI. Every facility is treated as unscaled.
                </div>
                <FormControlLabel
                  value={CUSTOMER_SITUATION.PAR360_ADDON}
                  control={<Radio />}
                  disabled={!par360Selected}
                  label="Existing scaled customer — add PAR 360 only"
                />
                <div className="client-details-solution-note">
                  Already-scaled hospitals. Existing scale savings stay in the baseline.
                  Requires PAR 360 Plus or Premium.
                  <div className="client-details-solution-example">e.g., Tampa General</div>
                </div>
                <FormControlLabel
                  value={CUSTOMER_SITUATION.MIXED}
                  control={<Radio />}
                  label="Mixed existing + new facilities"
                />
                <div className="client-details-solution-note">
                  Tag each hospital as already scaled or unscaled. New sites get a full
                  scale ROI; existing sites get PAR 360 only.
                  <div className="client-details-solution-example">e.g., Sentara</div>
                </div>
                <FormControlLabel
                  value={CUSTOMER_SITUATION.SCALE_EXPANSION}
                  control={<Radio />}
                  label="Existing sites adding more scales + PAR 360"
                />
                <div className="client-details-solution-note">
                  Additional scales at sites that already have them, plus PAR 360.
                  CAPEX for new hardware is Year 1 only.
                  <div className="client-details-solution-example">e.g., CHOP</div>
                </div>
              </RadioGroup>
              {situation === CUSTOMER_SITUATION.PAR360_ADDON && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Incremental PAR 360: daily scanning labor, order-line savings, and
                  existing scale subscription are excluded. Enter remaining overstock /
                  expiration after scales, not original pre-scale rates.
                </Alert>
              )}
              {situation === CUSTOMER_SITUATION.MIXED && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  On Spend Details, mark each facility Already scaled or Unscaled / new.
                  Only new scales create new scale savings and scale subscription.
                </Alert>
              )}
              {situation === CUSTOMER_SITUATION.SCALE_EXPANSION && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Enter existing vs new scales on each facility. New-scale labor and
                  order-line savings are incremental; PAR 360 applies to the footprint.
                  Put hardware purchase in Scale hardware (CAPEX) on Investment.
                </Alert>
              )}
            </div>
        </div>
      </div>
    </Slide>
  );
};
